import { Box, useTheme } from 'app/shopify/theme';
import { ActionPressable, type ActionProps } from '../shared/ActionPressable';
import { ActionIconSlot, ActionLabel, ActionSpinner, type ActionIcon } from '../shared/ActionContent';
import {
  buttonIconSize,
  buttonPadding,
  buttonPaddingVertical,
  buttonPresentation,
  type ButtonSize,
  type ButtonVariant,
} from '../shared/button-presentation';

export type { ButtonSize, ButtonVariant } from '../shared/button-presentation';
export type { ActionState as ButtonState } from '../shared/ActionPressable';
export type { ActionIcon, ActionIconProps } from '../shared/ActionContent';

export interface ButtonProps extends ActionProps {
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  inverted?: boolean;
  fullWidth?: boolean;
  /** Use a render function to receive the current token color and icon size. */
  leadingIcon?: ActionIcon;
  trailingIcon?: ActionIcon;
}

export function Button({
  label,
  variant = 'primary',
  size = 'medium',
  inverted = false,
  fullWidth = false,
  leadingIcon,
  trailingIcon,
  accessibilityLabel = label,
  ...props
}: ButtonProps) {
  const theme = useTheme();
  const textOnly = variant === 'textPrimary' || variant === 'textSecondary';
  const iconSize = Number(theme.tokens.sizing[buttonIconSize[size]]);
  const padding = textOnly ? 'spacing.general.0' : size === 'small' ? 'spacing.general.x1' : 'spacing.general.x2';
  return (
    <ActionPressable
      {...props}
      accessibilityLabel={accessibilityLabel}
      fullWidth={fullWidth}
      minHeight={textOnly ? undefined : Number(theme.tokens.sizing[`sizing.component.button.height.${size}`])}
      paddingHorizontal={textOnly ? 0 : theme.spacing[buttonPadding[size]]}
      paddingVertical={textOnly ? 0 : theme.spacing[buttonPaddingVertical[size]]}
      radius={textOnly ? theme.borderRadii['radius.xs'] : undefined}
      appearance={(interaction) => buttonPresentation(theme, variant, inverted, interaction)}
    >
      {(interaction) => {
        const visual = buttonPresentation(theme, variant, inverted, interaction);
        return (
          <>
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              maxWidth="100%"
              gap={textOnly ? 'spacing.general.x1' : 'spacing.general.0'}
              style={{ opacity: interaction.loading ? 0 : 1 }}
              aria-hidden={interaction.loading}
            >
              <ActionIconSlot icon={leadingIcon} size={iconSize} color={visual.foregroundColor} />
              <ActionLabel label={label} color={visual.foregroundColor} small={size !== 'large'} padding={padding} />
              <ActionIconSlot icon={trailingIcon} size={iconSize} color={visual.foregroundColor} />
            </Box>
            {interaction.loading ? (
              <Box
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                right={0}
                alignItems="center"
                justifyContent="center"
                pointerEvents="none"
              >
                <ActionSpinner color={visual.spinnerColor} size={size === 'large' ? 'large' : 'small'} />
              </Box>
            ) : null}
          </>
        );
      }}
    </ActionPressable>
  );
}
